import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Checkbox from "@material-ui/core/Checkbox";
import IconButton from "@material-ui/core/IconButton";
import Input from "@material-ui/core/Input";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";

import StarBorderIcon from "@material-ui/icons/StarBorder";
import StarIcon from "@material-ui/icons/Star";
import MailIcon from "@material-ui/icons/Mail";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import RefreshIcon from "@material-ui/icons/Refresh";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SearchIcon from "@material-ui/icons/Search";
import PaginationInput from "../models/pagination/PaginationInput";
import CloseIcon from "@material-ui/icons/Close";
import moment from "moment";
import jwtDecode from "jwt-decode";
import CustomDatePickerMui from "../models/inputs/DateInput";

import {
  fetchNotifications,
  fetchNotificationsSource,
  fetchNotificationById,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  toggleNotificationStar,
  clearNotificationsState,
  fetchNotificationCategories,
} from "../../../containers/actions/common/notificationsAction";

// ─── styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1.5, 3, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1, 1.5, 2),
    },
  },

  // ── top action row ─────────────────────────────────────────────────────────
  topRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3.5),
    [theme.breakpoints.down('xs')]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  searchGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexGrow: 1,
    minWidth: 0,
  },
  searchInput: {
    fontSize: "0.875rem",
    width: "100%",
    maxWidth: 460,
  },
  manageGroup: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  actionBtn: {
    backgroundColor: "#e36e39",
    color: "#fff",
    textTransform: "none",
    fontSize: "0.875rem",
    padding: "5px 18px",
    whiteSpace: "nowrap",
    borderRadius: "2rem",
    "&:hover": {
      backgroundColor: "#000",
      color: "#fff",
    },
  },

  // ── filter row ─────────────────────────────────────────────────────────────
  filterContainer: {
    padding: "0",
    marginBottom: theme.spacing(3.5),
  },
  filterLeft: {
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(2),
    },
  },
  filterGroup: {},
  filterGroupLabel: {
    fontWeight: "bold",
    marginBottom: 6,
    whiteSpace: "nowrap",
  },
  selectOutlined: {
    padding: theme.spacing(1),
  },
  filterRight: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 6,
    [theme.breakpoints.down('sm')]: {
      alignItems: "flex-start",
    },
  },
  syncRow: {
    display: "flex",
    alignItems: "center",
  },
  formPerPage: {
    minWidth: 60,
  },
  perPageGroup: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  dateRangeRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    [theme.breakpoints.down('xs')]: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 4,
    },
  },
  datePickerWrap: {
    width: 170,
    [theme.breakpoints.down('xs')]: {
      width: "100%",
    },
  },
  clearFiltersBtn: {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: '#e36e39',
    fontSize: '0.8rem',
    textDecoration: 'underline',
    whiteSpace: 'nowrap',
    alignSelf: 'flex-end',
    '&:hover': { color: '#000' },
  },

  markReadBtn: {
    background: "linear-gradient(82deg, #3B173F 0%, #073270 100%)",
    color: "#fff",
    textTransform: "none",
    fontSize: "0.875rem",
    padding: "5px 16px",
    whiteSpace: "nowrap",
    borderRadius: "2rem",
    "&:hover": {
      background: "linear-gradient(82deg, #2e1133 0%, #052558 100%)",
    },
    "&.Mui-disabled": {
      background: "#ccc",
      color: "#888",
    },
  },

  // ── table ──────────────────────────────────────────────────────────────────
  tableWrapper: {
    overflowX: "auto",
    width: "100%",
  },
  tablePaper: {
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    overflow: "hidden",
    minWidth: 800,
  },
  tableHead: {
    backgroundColor: "#fafafa",
    "& .MuiTableCell-head": {
      fontWeight: "bold",
      fontSize: "1rem",
      color: "#333",
      padding: "11px 12px",
      borderBottom: "2px solid #e0e0e0",
      whiteSpace: "nowrap",
    },
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "#f5f5f5",
      cursor: "pointer",
    },
    borderBottom: "1px solid #f0f0f0",
  },
  tableRowUnread: {
    backgroundColor: "#fafcff",
  },
  tableCell: {
    padding: "11px 12px",
    fontSize: "0.875rem",
    color: "#333",
    verticalAlign: "middle",
    borderBottom: "none",
  },
  iconCell: {
    padding: "6px 8px",
    width: 32,
    verticalAlign: "middle",
    borderBottom: "none",
  },
  eventName: {
    fontWeight: 600,
    fontSize: "0.875rem",
    color: "#222",
    display: "block",
    lineHeight: 1.4,
  },
  eventNameRead: {
    fontWeight: 400,
    color: "#555",
  },
  eventTime: {
    fontSize: "0.75rem",
    color: "#888",
    display: "block",
    marginTop: 3,
  },
  poNumber: {
    fontSize: "0.875rem",
    color: "#222",
  },
  cellValueUnread: {
    fontWeight: "bold",
  },
  yesText: {
    fontSize: "0.875rem",
    color: "#333",
  },
  noText: {
    fontSize: "0.875rem",
    color: "#bbb",
  },
  mailIconUnread: {
    color: "#c62828",
    fontSize: "1.35rem",
    verticalAlign: "middle",
  },
  starIcon: {
    color: "#f9a825",
    fontSize: "1.35rem",
    verticalAlign: "middle",
  },
  starBorderIcon: {
    color: "#bbb",
    fontSize: "1.35rem",
    verticalAlign: "middle",
  },
  expandIcon: {
    color: "#888",
    fontSize: "1.75rem",
    verticalAlign: "middle",
  },
  checkboxRoot: {
    padding: 3,
    "& svg": { fontSize: "1rem" },
  },

  // ── expanded detail ────────────────────────────────────────────────────────
  expandedRow: {
    backgroundColor: "#f4f6f9",
  },
  expandedContent: {},
  detailFields: {},
  detailFieldRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: 6,
    fontSize: "0.875rem",
  },
  detailFieldLabel: {
    fontSize: "0.875rem",
    color: "#333",
    minWidth: 120,
    fontWeight: "bold",
  },
  detailFieldColon: {
    fontSize: "0.875rem",
    color: "#555",
    marginRight: 8,
  },
  detailFieldValue: {
    fontSize: "0.875rem",
    color: "#333",
  },
  viewPoBtn: {
    backgroundColor: "#e36e39",
    color: "#fff",
    textTransform: "none",
    fontSize: "0.875rem",
    padding: "3px 16px",
    borderRadius: "2rem",
    whiteSpace: "nowrap",
    marginLeft: theme.spacing(2),
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "#000",
      color: "#fff",
    },
  },

  // ── mark-all confirm dialog ────────────────────────────────────────────────
  confirmDialogPaper: {
    borderRadius: 12,
    padding: theme.spacing(3, 4, 4),
    maxWidth: 480,
    position: 'relative',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2, 2, 3),
    },
  },
  confirmDialogCancelRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 4,
    marginBottom: theme.spacing(2),
  },
  confirmDialogCancelText: {
    fontSize: '0.9rem',
    color: '#333',
    fontWeight: 500,
    cursor: 'pointer',
    lineHeight: 1,
  },
  confirmDialogTitle: {
    fontWeight: 700,
    fontSize: '1.2rem',
    textAlign: 'center',
    marginBottom: theme.spacing(2),
  },
  confirmDialogBody: {
    fontSize: '0.9rem',
    color: '#555',
    textAlign: 'center',
    marginBottom: theme.spacing(3),
    lineHeight: 1.6,
  },
  confirmDialogBtn: {
    display: 'block',
    margin: '0 auto',
    color: '#fff',
    textTransform: 'none',
    fontSize: '0.9rem',
    padding: '6px 36px',
    borderRadius: '2rem',
    background: 'linear-gradient(82deg, #3B173F 0%, #073270 100%)',
    '&:hover': {
      background: 'linear-gradient(82deg, #2e1133 0%, #052558 100%)',
    },
  },
}));

// ─── helpers ──────────────────────────────────────────────────────────────────

const formatRelativeTime = (isoString) => {
  if (!isoString) return '';
  const diffMs = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min${mins !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months !== 1 ? 's' : ''} ago`;
};

// ─── constants ────────────────────────────────────────────────────────────────

// Maps API enum value → display label for category filter.
//
// BCM-220 re-categorization — display labels match the 6 business-facing
// category names. The filter dropdown in the dashboard is built dynamically
// from DISTINCT event_category values present in the DB (via the
// /fetchNotificationCategories endpoint), so users only see categories that
// actually have notifications. No hardcoded filter list.
//
// In prod today only 3 of these will ever appear (PO Collaboration, Batch
// Execution, Batch Allocation by BMS — covering 35 prod events). The other
// 3 categories are ready for future events (User Management,
// Batch Allocation By CMO, Inventory Reconciliation) without needing a FE
// change when their first event fires.
const CATEGORY_DISPLAY = {
  PO_MANAGEMENT: "PO Collaboration",
  BATCH_MANUFACTURING: "Batch Execution",
  INVENTORY_RECONCILIATION: "Inventory Reconciliation",
  USER_MANAGEMENT: "User Management",
  BATCH_ALLOCATION_BY_CMO: "Batch Allocation By CMO",
  BATCH_ALLOCATION_BY_BMS: "Batch Allocation by BMS",
  // SAP_RELATED and FORECAST_PLANNING remain in the enum but no events use
  // them — intentionally not listed here so if they ever re-appear in DB
  // data, the raw enum string is shown in the filter (visible signal of
  // unexpected data) rather than a pretty label.
  OTHER: "Others",
};


// ─── helpers ──────────────────────────────────────────────────────────────────

/**
 * Convert a camelCase event code to a human-readable title-cased string.
 * e.g. "allocationChangedByCMO" → "Allocation Changed By CMO"
 *      "singleBatchAllocation"  → "Single Batch Allocation"
 */
function formatEventCode(code) {
  if (!code) return 'NA';
  return code
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')  // e.g. "CMOUser" → "CMO User"
    .replace(/([a-z])([A-Z])/g, '$1 $2')          // e.g. "batchAllocation" → "batch Allocation"
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Acronyms that must always render in full caps in event names / labels.
// Keep in sync with ManageDistributionList / ManageNotifications / EventEditViewDialog.
const EVENT_NAME_ACRONYMS = new Set([
  'BMS', 'CMO', 'ABO', 'PO', 'EU', 'ASN', 'SAP', 'API',
  'GRN', 'PCMP', 'ERP', 'UOM', 'QC', 'QA', 'CC', 'SL', 'ID',
]);

// Title-case an event name and force acronyms (BMS, CMO, ABO, PO, EU, ASN, …) to full caps.
const formatEventName = (name) => {
  if (!name) return '';
  return name
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')   // "CMOUser" -> "CMO User"
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')        // "batchAllocation" -> "batch Allocation"
    .replace(/[_-]+/g, ' ')                          // snake_case / kebab -> spaces
    .trim()
    .split(/\s+/)
    .map((w) => {
      const upper = w.toUpperCase();
      if (EVENT_NAME_ACRONYMS.has(upper)) return upper;            // BMS, CMO, ABO, PO, EU, ASN...
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(); // Title Case each word
    })
    .join(' ');
};

// ─── component ────────────────────────────────────────────────────────────────

const NotificationsDashboard = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  // Show "Manage Distribution List" only for BMS Admin users.
  const isBmsAdmin = localStorage.collabType !== 'CMO' && localStorage.roleName === 'Admin';
  const isBmsUser = localStorage.collabType !== 'CMO';

  // ── Redux state ───────────────────────────────────────────────────────────
  const {
    notifications,
    pagination,
    lastSyncedAt,
    fetchLoading,
    categories: dbCategories,
    unreadCount,
  } = useSelector((state) => state.pcmp.common.notifications);

  // CMO list for CMO filter (BMS side only)
  const cmosDetails = useSelector((state) => state.pcmp.common.inventoryStatus?.cmosDetails);

  // Dynamic filter options from DB — with display label mapping.
  // Only categories present in CATEGORY_DISPLAY are shown. Unmapped enums
  // (e.g. legacy SAP_RELATED / FORECAST_PLANNING rows) are hidden from the
  // filter dropdown per UAT ask.
  const FILTER_OPTIONS = useMemo(() => (dbCategories || [])
    .filter((cat) => CATEGORY_DISPLAY[cat])
    .map((cat) => CATEGORY_DISPLAY[cat]),
    [dbCategories]);

  // Reverse map: display label → API enum value (mapped categories only)
  const CATEGORY_MAP = useMemo(() => {
    const map = {};
    (dbCategories || []).forEach((cat) => {
      if (CATEGORY_DISPLAY[cat]) {
        map[CATEGORY_DISPLAY[cat]] = cat;
      }
    });
    return map;
  }, [dbCategories]);

  // CMO filter options (BMS side only)
  // API returns { status, CMOMembers: [names], CMOMembersWithId: [{ organization_id, organization_name, ... }] }
  const cmoOptions = useMemo(() => {
    if (!isBmsUser || !cmosDetails?.CMOMembersWithId) return [];
    return cmosDetails.CMOMembersWithId.map((c) => ({
      id: c.organization_id,
      name: c.organization_name || `CMO ${c.organization_id}`,
    }));
  }, [isBmsUser, cmosDetails]);

  // ── local UI state ────────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [viewBy, setViewBy] = useState("All");
  const [filterBy, setFilterBy] = useState([]);
  const [selectedCmo, setSelectedCmo] = useState("");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState({});
  const [markAllConfirmOpen, setMarkAllConfirmOpen] = useState(false);

  // ── build params — always send all fields, null for unselected (app-wide pattern) ─
  // Format a Date as YYYY-MM-DD using LOCAL calendar parts. Avoids toISOString()
  // which shifts to UTC and causes off-by-one bugs (e.g. 11-Apr IST → 10-Apr UTC).
  const toLocalYmd = (d) => {
    if (!d) return null;
    const dt = d instanceof Date ? d : new Date(d);
    if (isNaN(dt.getTime())) return null;
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const buildFetchParams = () => ({
    page: currentPage,
    page_size: perPage,
    // is_read: null = All/Starred/Unstarred (no server filter); true/false for Read/Unread
    is_read: viewBy === "Read" ? true : viewBy === "Unread" ? false : null,
    // starred: server-side filter (previously client-side which broke pagination counts)
    starred: viewBy === "Starred" ? true : viewBy === "Unstarred" ? false : null,
    // categories: send array when 1+ selected but not all; null = no filter (all)
    categories: filterBy.length > 0 && filterBy.length < FILTER_OPTIONS.length
      ? filterBy.map((f) => CATEGORY_MAP[f])
      : null,
    search: appliedSearch || null,
    // Send YYYY-MM-DD (local calendar day). BE casts to ::date for timezone-free compare.
    date_from: toLocalYmd(dateFrom),
    date_to: toLocalYmd(dateTo),
    sort_by: "notified_at",
    sort_order: "desc",
    vendor_mosymphony_id: selectedCmo || null,
  });

  // ── log auth token on mount ───────────────────────────────────────────────
  useEffect(() => {
    try {
      const decoded = jwtDecode(localStorage.authToken);
      console.log('[NotificationsDashboard] authToken decoded:', {
        saas_user_id: decoded.saas_user_id,
        full: decoded,
      });
    } catch (e) {
      console.warn('[NotificationsDashboard] Could not decode authToken:', e);
    }
    try {
      const decodedSauth = jwtDecode(localStorage.sauth);
      console.log('[NotificationsDashboard] sauth decoded:', {
        saas_user_id: decodedSauth.saas_user_id,
        full: decodedSauth,
      });
    } catch (e) {
      console.warn('[NotificationsDashboard] Could not decode sauth:', e);
    }
  }, []);

  // ── fetch categories once on mount (static — no API) ──────────────────────
  // CMO list fetch removed: the notifications module makes no real API calls on
  // this branch. The CMO filter simply has no options.
  useEffect(() => {
    dispatch(fetchNotificationCategories());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── fetch whenever page, perPage, or server-side filters change ──────────
  useEffect(() => {
    dispatch(fetchNotifications(buildFetchParams()));
    dispatch(fetchUnreadCount());
    return () => {
      if (fetchNotificationsSource) fetchNotificationsSource.cancel();
    };
  }, [currentPage, perPage, viewBy, filterBy, appliedSearch, dateFrom, dateTo, selectedCmo]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── helpers ───────────────────────────────────────────────────────────────
  const toggleExpand = (id) => {
    const wasExpanded = expandedRows[id];
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
    if (!wasExpanded) {
      // Expanding: fetch fresh detail data for this notification
      dispatch(fetchNotificationById({ id }));
      // Mark as read if it was unread
      const notif = notifications.find((n) => n.id === id);
      if (notif && !notif.is_read) {
        dispatch(markNotificationRead({ id }));
      }
    }
  };

  const toggleStar = (e, id) => {
    e.stopPropagation();
    dispatch(toggleNotificationStar({ id }));
  };

  // ND-014: Action button navigation based on object_type or event_category
  const handleActionButtonClick = (e, n) => {
    e.stopPropagation();
    const base = `/pcmp/${localStorage.collabType}/${localStorage.roleName}/${localStorage.organizationName}/${localStorage.ecosystemId}`;
    const type = (n.object_type || '').toLowerCase();
    const cat = (n.event_category || '').toLowerCase();
    const entityRef = n.entity_ref || n.object_id || '';
    const p = n.payload || {};
    const poNumber = n.po_number || p.poNumber || p.scheduleAgreementNumber
      || p.grnEventPayload?.GRNSapRequest?.scheduleAgreementNumber || '';
    const batchNumber = n.batch_number || p.batchNumber || p.vendorBatchNumber
      || p.grnEventPayload?.GRNSapRequest?.vendorBatchNumber || '';

    if (type.includes('po') || type.includes('purchase') || cat === 'po_management') {
      // PO Collaboration
      history.push(`${base}/purchaseorderManagement`);
    } else if (type.includes('batch') || cat === 'batch_manufacturing') {
      // Batch Execution
      history.push(`${base}/fgBatches`);
    } else if (type.includes('inventory') || cat === 'inventory_reconciliation') {
      // Inventory Reconciliation
      history.push(`${base}/inventoryStatus`);
    } else if (cat === 'batch_allocation_by_bms' || cat === 'batch_allocation_by_cmo') {
      // Batch allocations happen at the PO level — land user on PO screen so
      // they can find the specific PO and open the allocation tab.
      history.push(`${base}/purchaseorderManagement`);
    } else if (cat === 'user_management') {
      // User Management (e.g. userRoleChange) — land on user's profile page.
      history.push(`${base}/userProfile`);
    } else {
      history.push(`${base}/purchaseorderManagement`);
    }
  };

  const handleMarkAllRead = () => {
    // category: null = mark ALL; pass a category string to scope to one category
    dispatch(markAllNotificationsRead({ category: null }));
    dispatch(clearNotificationsState());
  };

  const handleSearch = () => {
    setAppliedSearch(search);
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    dispatch(fetchNotifications(buildFetchParams()));
    dispatch(fetchUnreadCount());
  };

  const handleClearFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setViewBy("All");
    setFilterBy([]);
    setSelectedCmo("");
    setDateFrom(null);
    setDateTo(null);
    setCurrentPage(1);
  };

  const hasActiveFilters =
    appliedSearch !== "" ||
    viewBy !== "All" ||
    filterBy.length > 0 ||
    selectedCmo !== "" ||
    dateFrom !== null ||
    dateTo !== null;

  // All filters (search, read, category, starred, date) are now handled server-side
  // so totalCount / pagination / Per Page reflect the full filtered set.
  const filtered = notifications;

  const totalPages = pagination?.totalPages || 1;
  const totalCount = pagination?.total || 0;

  return (
    <div className={classes.root}>

      {/* ── top action row ───────────────────────────────────────────────── */}
      <div className={classes.topRow}>
        {/* Search group (left) */}
        <div className={classes.searchGroup}>
          <Input
            className={classes.searchInput}
            placeholder="Search using Event, PO Number, Product Code / Description"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            startAdornment={
              <SearchIcon style={{ color: "#888", fontSize: "1.1rem", marginRight: 4 }} />
            }
            endAdornment={
              search ? (
                <IconButton
                  size="small"
                  style={{ padding: 2 }}
                  onClick={() => { setSearch(""); setAppliedSearch(""); setCurrentPage(1); }}
                >
                  <CloseIcon style={{ fontSize: "1rem", color: "#888" }} />
                </IconButton>
              ) : null
            }
          />
          <Button
            className={classes.actionBtn}
            onClick={handleSearch}
            variant="contained"
            disableElevation
          >
            Search
          </Button>
        </div>

        {/* Manage buttons group (right) */}
        <div className={classes.manageGroup}>
          {isBmsAdmin && (
            <Button
              className={classes.actionBtn}
              variant="contained"
              disableElevation
              onClick={() => history.push(location.pathname.replace(/\/notifications$/, '/manage-distribution-list'))}
            >
              Manage Distribution List
            </Button>
          )}
          <Button
            className={classes.actionBtn}
            variant="contained"
            disableElevation
            onClick={() => history.push(location.pathname.replace(/\/notifications$/, '/manage-notifications'))}
          >
            Manage Notifications
          </Button>
        </div>
      </div>

      {/* ── filter row ───────────────────────────────────────────────────── */}
      <Grid container justifyContent="space-between" alignItems="flex-start" className={classes.filterContainer}>

        {/* ── Left: all filter controls ─────────────────────────────────── */}
        <Grid item xs={12} md>
          <div className={classes.filterLeft}>

            {/* View by */}
            <div className={classes.filterGroup}>
              <Typography className={classes.filterGroupLabel}>View by</Typography>
              <FormControl style={{ minWidth: 160 }}>
                <Select
                  value={viewBy}
                  onChange={(e) => { setViewBy(e.target.value); setCurrentPage(1); }}
                  variant="outlined"
                  classes={{ outlined: classes.selectOutlined }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Read">Read</MenuItem>
                  <MenuItem value="Unread">Unread</MenuItem>
                  <MenuItem value="Starred">Starred</MenuItem>
                  <MenuItem value="Unstarred">Unstarred</MenuItem>
                </Select>
              </FormControl>
            </div>

            {/* Filter by category */}
            <div className={classes.filterGroup}>
              <Typography className={classes.filterGroupLabel}>Filter by category</Typography>
              <FormControl style={{ minWidth: 180 }}>
                <Select
                  multiple
                  displayEmpty
                  value={filterBy}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val[val.length - 1] === "__all__") {
                      setFilterBy(filterBy.length === FILTER_OPTIONS.length ? [] : [...FILTER_OPTIONS]);
                    } else {
                      setFilterBy(val.filter((v) => v !== "__all__"));
                    }
                    setCurrentPage(1);
                  }}
                  variant="outlined"
                  classes={{ outlined: classes.selectOutlined }}
                  renderValue={(selected) =>
                    selected.length === 0 || selected.length === FILTER_OPTIONS.length
                      ? "All"
                      : selected.length === 1
                      ? selected[0]
                      : "Multiple selection"
                  }
                >
                  <MenuItem value="__all__">
                    <Checkbox
                      size="small"
                      checked={filterBy.length === FILTER_OPTIONS.length}
                      indeterminate={filterBy.length > 0 && filterBy.length < FILTER_OPTIONS.length}
                      classes={{ root: classes.checkboxRoot }}
                    />
                    All
                  </MenuItem>
                  {FILTER_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      <Checkbox
                        size="small"
                        checked={filterBy.includes(option)}
                        classes={{ root: classes.checkboxRoot }}
                      />
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* Filter by CMO (BMS side only) */}
            {isBmsUser && cmoOptions.length > 0 && (
              <div className={classes.filterGroup}>
                <Typography className={classes.filterGroupLabel}>CMO</Typography>
                <FormControl style={{ minWidth: 180 }}>
                  <Select
                    displayEmpty
                    value={selectedCmo}
                    onChange={(e) => { setSelectedCmo(e.target.value); setCurrentPage(1); }}
                    variant="outlined"
                    classes={{ outlined: classes.selectOutlined }}
                    MenuProps={{ PaperProps: { style: { maxHeight: 250 } } }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {cmoOptions.map((cmo) => (
                      <MenuItem key={cmo.id} value={cmo.id}>{cmo.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            )}

            {/* Filter by date */}
            <div className={classes.filterGroup}>
              <Typography className={classes.filterGroupLabel}>Filter by date</Typography>
              <div className={classes.dateRangeRow}>
                <div className={classes.datePickerWrap}>
                  <CustomDatePickerMui
                    name="FromDate"
                    value={dateFrom}
                    onChange={(date) => { setDateFrom(date); setCurrentPage(1); }}
                    maxDate={dateTo || new Date()}
                    inputProps={{ style: { padding: "8px 0 8px 5px" } }}
                  />
                </div>
                <Typography style={{ color: "#555" }}>to</Typography>
                <div className={classes.datePickerWrap}>
                  <CustomDatePickerMui
                    name="ToDate"
                    value={dateTo}
                    onChange={(date) => { setDateTo(date); setCurrentPage(1); }}
                    minDate={dateFrom || undefined}
                    maxDate={new Date()}
                    inputProps={{ style: { padding: "8px 0 8px 5px" } }}
                  />
                </div>
              </div>
            </div>

            {/* Mark all as read */}
            <Button
              className={classes.markReadBtn}
              variant="contained"
              disableElevation
              startIcon={<CheckCircleIcon style={{ fontSize: "1rem" }} />}
              onClick={() => setMarkAllConfirmOpen(true)}
              disabled={!unreadCount}
            >
              Mark all as read
            </Button>

            {/* Clear all filters */}
            {hasActiveFilters && (
              <button className={classes.clearFiltersBtn} onClick={handleClearFilters}>
                Clear all filters
              </button>
            )}

          </div>
        </Grid>

        {/* ── Right: Last Synced + Per Page ─────────────────────────────── */}
        <Grid item xs={12} md="auto">
          <div className={classes.filterRight}>
            {/* Last Synced */}
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="body2" className="last-synced-text">
                {`Last Synced @ ${moment(lastSyncedAt || new Date()).format("HH:mm:ss; DD MMMM, YYYY")}`}
              </Typography>
              <Typography
                variant="body2"
                color="primary"
                className="refresh-button"
                style={{ cursor: "pointer" }}
                onClick={handleRefresh}
              >
                Refresh <RefreshIcon />
              </Typography>
            </Box>

            {/* Per Page */}
            <div className={classes.perPageGroup}>
              <Typography className={classes.filterGroupLabel} style={{ marginBottom: 0, marginRight: 8 }}>
                PER PAGE
              </Typography>
              <FormControl className={classes.formPerPage}>
                <Select
                  value={perPage}
                  onChange={(e) => { setPerPage(e.target.value); setCurrentPage(1); }}
                  variant="outlined"
                  classes={{ outlined: classes.selectOutlined }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>
        </Grid>

      </Grid>

      {/* ── table ────────────────────────────────────────────────────────── */}
      <div className={classes.tableWrapper}>
      <Paper className={classes.tablePaper} elevation={0}>
        <Table size="small">
          <TableHead className={classes.tableHead}>
            <TableRow>
              {/* expand */}
              <TableCell style={{ width: 32, padding: "10px 6px" }} />
              {/* star + mail */}
              <TableCell style={{ width: 64, padding: "10px 6px" }} />
              <TableCell>Event</TableCell>
              <TableCell>PO Number</TableCell>
              <TableCell>Product Code / Description</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {fetchLoading && (
              <TableRow>
                <TableCell colSpan={5} align="center" style={{ padding: 32 }}>
                  <CircularProgress color="primary" thickness={5} />
                </TableCell>
              </TableRow>
            )}
            {!fetchLoading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" style={{ padding: 32, color: "#888", fontSize: "0.875rem" }}>
                  No notifications found.
                </TableCell>
              </TableRow>
            )}

            {!fetchLoading && filtered.map((n) => {
              console.log('DEBUG notification:', n.title, { po_number: n.po_number, product_code: n.product_code, payload_type: typeof n.payload, payload_poNumber: n.payload?.poNumber, payload_productCode: n.payload?.productCode });
              const poNumber = n.po_number || n.payload?.poNumber || n.payload?.scheduleAgreementNumber || n.payload?.po_number || n.entity_ref || "NA";
              const fgCode = n.product_code || n.payload?.productCode || n.payload?.fgMaterialCode || null;
              const fgDesc = n.product_description || n.payload?.productDescription || n.payload?.fgMaterialName || null;
              const productDisplay = fgCode && fgDesc
                ? `${fgCode} / ${fgDesc}`
                : fgCode || fgDesc || "NA";

              return (
                <React.Fragment key={n.id}>
                  {/* ── collapsed row ── */}
                  <TableRow
                    className={`${classes.tableRow} ${!n.is_read ? classes.tableRowUnread : ""}`}
                    onClick={() => toggleExpand(n.id)}
                  >
                    {/* expand icon */}
                    <TableCell className={classes.iconCell}>
                      {expandedRows[n.id] ? (
                        <KeyboardArrowDownIcon className={classes.expandIcon} />
                      ) : (
                        <KeyboardArrowRightIcon className={classes.expandIcon} />
                      )}
                    </TableCell>

                    {/* star + mail */}
                    <TableCell className={classes.iconCell} style={{ width: 64 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <Tooltip title={n.is_starred ? "Starred" : "Not starred"} arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => toggleStar(e, n.id)}
                            style={{ padding: 2 }}
                          >
                            {n.is_starred ? (
                              <StarIcon className={classes.starIcon} />
                            ) : (
                              <StarBorderIcon className={classes.starBorderIcon} />
                            )}
                          </IconButton>
                        </Tooltip>
                        {!n.is_read && <MailIcon className={classes.mailIconUnread} />}
                      </div>
                    </TableCell>

                    {/* event */}
                    <TableCell className={classes.tableCell}>
                      <span className={`${classes.eventName} ${n.is_read ? classes.eventNameRead : ""}`}>
                        {formatEventName(n.title)}
                      </span>
                      <span className={classes.eventTime}>{formatRelativeTime(n.notified_at)}</span>
                    </TableCell>

                    {/* PO number */}
                    <TableCell className={classes.tableCell}>
                      <span className={`${classes.poNumber}${!n.is_read ? ` ${classes.cellValueUnread}` : ""}`}>
                        {poNumber}
                      </span>
                    </TableCell>

                    {/* product code / description */}
                    <TableCell className={classes.tableCell}>
                      <span className={!n.is_read ? classes.cellValueUnread : ""}>
                        {productDisplay}
                      </span>
                    </TableCell>

                  </TableRow>

                  {/* ── expanded detail row ── */}
                  {expandedRows[n.id] && (
                    <TableRow className={classes.expandedRow}>
                      <TableCell colSpan={2} style={{ padding: 0, borderBottom: "1px solid #e0e0e0" }} />
                      <TableCell colSpan={3} style={{ padding: "14px 16px 18px 12px", borderBottom: "1px solid #e0e0e0" }}>
                        <div className={classes.detailFields}>
                          {/* All sub-data fields always visible — show "NA" when no value
                              (per UAT feedback: fields must not hide based on event_category). */}
                          <div className={classes.detailFieldRow}>
                            <span className={classes.detailFieldLabel}>PO Status</span>
                            <span className={classes.detailFieldColon}>&nbsp;:&nbsp;</span>
                            <span className={classes.detailFieldValue}>{n.payload?.statusDisplayName || n.payload?.poStatus || "NA"}</span>
                          </div>
                          <div className={classes.detailFieldRow}>
                            <span className={classes.detailFieldLabel}>Batch Status</span>
                            <span className={classes.detailFieldColon}>&nbsp;:&nbsp;</span>
                            <span className={classes.detailFieldValue}>{n.payload?.batchStatus || n.payload?.batchStatusDisplayName || "NA"}</span>
                          </div>
                          <div className={classes.detailFieldRow}>
                            <span className={classes.detailFieldLabel}>Batch Number</span>
                            <span className={classes.detailFieldColon}>&nbsp;:&nbsp;</span>
                            <span className={classes.detailFieldValue}>{n.batch_number || n.payload?.batchNumber || n.payload?.vendorBatchNumber || n.payload?.grnEventPayload?.GRNSapRequest?.vendorBatchNumber || "NA"}</span>
                          </div>
                          {isBmsUser && n.vendor_mosymphony_id && (
                          <div className={classes.detailFieldRow}>
                            <span className={classes.detailFieldLabel}>CMO</span>
                            <span className={classes.detailFieldColon}>&nbsp;:&nbsp;</span>
                            <span className={classes.detailFieldValue}>{n.cmoName || n.vendor_mosymphony_id}</span>
                          </div>
                          )}
                          <div className={classes.detailFieldRow}>
                            <span className={classes.detailFieldLabel}>Organization</span>
                            <span className={classes.detailFieldColon}>&nbsp;:&nbsp;</span>
                            <span className={classes.detailFieldValue}>{n.organizationName || "NA"}</span>
                          </div>
                          <div className={classes.detailFieldRow}>
                            <span className={classes.detailFieldLabel}>Role</span>
                            <span className={classes.detailFieldColon}>&nbsp;:&nbsp;</span>
                            <span className={classes.detailFieldValue}>{n.roleName || "NA"}</span>
                          </div>
                          <div className={classes.detailFieldRow}>
                            <span className={classes.detailFieldLabel}>Username</span>
                            <span className={classes.detailFieldColon}>&nbsp;:&nbsp;</span>
                            <span className={classes.detailFieldValue}>{n.payload?.lastUpdatedByName || n.payload?.helperFields?.userName || n.payload?.lastUpdatedBy || (n.created_by && n.created_by !== 'UNKNOWN' ? n.created_by : null) || "NA"}</span>
                          </div>
                          {n.has_action_link && n.action_label && (
                            <Button
                              className={classes.viewPoBtn}
                              variant="contained"
                              disableElevation
                              size="small"
                              style={{ marginLeft: 0, marginTop: 8 }}
                              onClick={(e) => handleActionButtonClick(e, n)}
                            >
                              {n.action_label}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
      </div>

      {/* ── pagination ───────────────────────────────────────────────────── */}
      <Grid container alignItems="center" justifyContent="center" style={{ paddingTop: "1rem" }}>
        <PaginationInput
          totalPages={totalPages}
          handlePageChange={(page) => setCurrentPage(page)}
          page={currentPage}
        />
      </Grid>

      {/* ── mark-all-read confirmation dialog ─────────────────────────── */}
      <Dialog
        open={markAllConfirmOpen}
        onClose={() => setMarkAllConfirmOpen(false)}
        PaperProps={{ className: classes.confirmDialogPaper }}
      >
        <DialogContent style={{ padding: 0 }}>
          <div className={classes.confirmDialogCancelRow}>
            <Typography
              className={classes.confirmDialogCancelText}
              onClick={() => setMarkAllConfirmOpen(false)}
            >
              Cancel
            </Typography>
            <IconButton
              size="small"
              style={{ padding: 2, color: '#d32f2f' }}
              onClick={() => setMarkAllConfirmOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          <Typography className={classes.confirmDialogTitle}>
            Mark all notifications as read?
          </Typography>
          <Typography className={classes.confirmDialogBody}>
            This will mark all your notifications as read.<br />
            This action cannot be undone.
          </Typography>
          <Button
            className={classes.confirmDialogBtn}
            variant="contained"
            disableElevation
            onClick={() => {
              handleMarkAllRead();
              setMarkAllConfirmOpen(false);
            }}
          >
            Confirm
          </Button>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default NotificationsDashboard;
